import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Printer, DownloadSimple, ShieldCheck, Wallet, Certificate, CircleNotch } from "@phosphor-icons/react";

// The wall-worthy Certificate hub: preview + print/download the full gold-seal
// certificate and/or the wallet-sized card. The live anti-screenshot verification
// screen lives on its own pathway at /verification/:id.
export default function CertificatePrintPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Staff who took the course "as a grade" keep that context on the way back.
  const gradeQS = searchParams.get("grade") ? `?grade=${searchParams.get("grade")}` : "";
  const { user } = useAuth();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState("");
  const [previews, setPreviews] = useState({ full: null, wallet: null });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/${id}`);
        setCourse(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  useEffect(() => {
    if (!course || course.progress_percent < 100) return;
    let cancelled = false;
    (async () => {
      for (const variant of ["full", "wallet"]) {
        try {
          const { data } = await api.get(`/certificate/${id}/pdf?variant=${variant}&format=png`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(new Blob([data], { type: "image/png" }));
          setPreviews(p => ({ ...p, [variant]: url }));
        } catch (_) { /* preview optional */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [course?.progress_percent, id]);

  if (!user) return null;
  if (err) return <div className="p-8 text-[#FF5100]">{err}</div>;
  if (!course) return <div className="p-8 text-[#000000]">Loading...</div>;

  if (course.progress_percent < 100) {
    return (
      <div className="bg-grain min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <h2 className="text-2xl font-bold font-display mb-3">Not yet certified</h2>
        <p className="text-[#000000] mb-5">Finish all lessons and quizzes to unlock your certificate.</p>
        <button data-testid="back-to-course-button" className="btn-primary" onClick={() => nav(`/course/${id}${gradeQS}`)}>Back to course</button>
      </div>
    );
  }

  const printImages = (urls) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<html><head><title>Print certificate</title><style>body{margin:0;text-align:center}img{max-width:100%;max-height:95vh;page-break-after:always;margin:12px auto;display:block}</style></head><body>` +
      urls.filter(Boolean).map(u => `<img src="${u}" />`).join("") +
      `<script>window.onload=function(){setTimeout(function(){window.print()},400)}<\/script></body></html>`);
    w.document.close();
  };

  const downloadPdf = async (variant) => {
    setBusy(variant);
    try {
      const { data } = await api.get(`/certificate/${id}/pdf?variant=${variant}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${variant === "wallet" ? "wallet-card" : "certificate"}-${(id || "").slice(0, 6)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-grain min-h-screen">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <button data-testid="cert-print-back-button" className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold cursor-pointer"
            style={{ background: "#FFFFFF", color: "#000000", border: "1px solid #C4C8CE" }}
            onClick={() => nav("/dashboard")}>
            <ArrowLeft size={16} weight="bold" /> Back
          </button>
          <button data-testid="goto-verification-button" className="btn-ghost flex items-center gap-1.5 text-sm text-[#0A6A2C] font-bold" onClick={() => nav(`/verification/${id}${gradeQS}`)}>
            <ShieldCheck size={18} weight="fill" /> Live completion verification →
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.25em] text-[#8fb300] font-bold mb-1">Course complete · 100%</div>
          <h1 className="text-4xl font-extrabold font-display mb-6 flex items-center gap-3">
            <Certificate size={38} weight="fill" color="#FFC400" /> Your Certificate
          </h1>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Full certificate */}
            <div className="card" data-testid="full-cert-card">
              <div className="text-sm font-bold font-display mb-2 flex items-center gap-2">
                <Certificate size={18} weight="duotone" /> Wall certificate
              </div>
              {previews.full
                ? <img src={previews.full} alt="Certificate preview" className="rounded-xl w-full border border-[#C4C8CE]" data-testid="full-cert-preview" />
                : <div className="h-44 rounded-xl bg-[#F4F4F5] animate-pulse grid place-items-center" data-testid="full-cert-loading">
                    <div className="flex flex-col items-center gap-2 text-[#000000]">
                      <CircleNotch size={22} className="animate-spin" />
                      <span className="text-xs font-semibold">Preparing your certificate…</span>
                    </div>
                  </div>}
              <div className="flex gap-2 mt-3 flex-wrap">
                <button data-testid="print-full-cert" className="btn-primary text-sm flex items-center gap-1.5" onClick={() => printImages([previews.full])} disabled={!previews.full}>
                  <Printer size={16} weight="bold" /> Print
                </button>
                <button data-testid="download-full-cert" className="btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50" disabled={busy === "full"} onClick={() => downloadPdf("full")}>
                  <DownloadSimple size={16} weight="bold" /> {busy === "full" ? "Preparing…" : "Download PDF"}
                </button>
              </div>
            </div>

            {/* Wallet card */}
            <div className="card" data-testid="wallet-cert-card">
              <div className="text-sm font-bold font-display mb-2 flex items-center gap-2">
                <Wallet size={18} weight="duotone" /> Wallet card
              </div>
              {previews.wallet
                ? <img src={previews.wallet} alt="Wallet card preview" className="rounded-xl border border-[#C4C8CE] mx-auto" style={{ maxWidth: "320px", width: "100%" }} data-testid="wallet-cert-preview" />
                : <div className="h-44 rounded-xl bg-[#F4F4F5] animate-pulse grid place-items-center" data-testid="wallet-cert-loading">
                    <div className="flex flex-col items-center gap-2 text-[#000000]">
                      <CircleNotch size={22} className="animate-spin" />
                      <span className="text-xs font-semibold">Preparing your wallet card…</span>
                    </div>
                  </div>}
              <div className="flex gap-2 mt-3 flex-wrap">
                <button data-testid="print-wallet-cert" className="btn-primary text-sm flex items-center gap-1.5" onClick={() => printImages([previews.wallet])} disabled={!previews.wallet}>
                  <Printer size={16} weight="bold" /> Print
                </button>
                <button data-testid="download-wallet-cert" className="btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50" disabled={busy === "wallet"} onClick={() => downloadPdf("wallet")}>
                  <DownloadSimple size={16} weight="bold" /> {busy === "wallet" ? "Preparing…" : "Download PDF"}
                </button>
              </div>
            </div>
          </div>

          <button data-testid="print-both-certs" className="btn-primary mt-6 flex items-center gap-2 text-lg px-6 py-3"
            onClick={() => printImages([previews.full, previews.wallet])} disabled={!previews.full && !previews.wallet}>
            <Printer size={20} weight="bold" /> Print both
          </button>
        </motion.div>
      </div>
    </div>
  );
}
